import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Checkbox
 *
 * A branded checkbox with gold focus ring, navy check, and animation.
 * Respects prefers-reduced-motion via framer-motion.
 *
 * Usage:
 *   <Checkbox checked={agreed} onChange={setAgreed} label="I agree to the terms" />
 *   <Checkbox indeterminate label="Select all" />
 *
 *   // Group
 *   <CheckboxGroup label="Lunch Options">
 *     <Checkbox value="chicken" label="Chicken Caesar" />
 *     <Checkbox value="pasta" label="Pasta Primavera" />
 *   </CheckboxGroup>
 */

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
	/** Whether checked */
	checked?: boolean
	/** Indeterminate state (overrides checked visual) */
	indeterminate?: boolean
	/** Change handler */
	onChange?: (checked: boolean) => void
	/** Label text */
	label?: string
	/** Helper/description text */
	hint?: string
	/** Error message */
	error?: string
	/** Size */
	size?: 'sm' | 'md' | 'lg'
}

const sizeStyles = {
	sm: { box: 'w-4 h-4 rounded', icon: 12, label: 'text-xs' },
	md: { box: 'w-5 h-5 rounded-md', icon: 14, label: 'text-sm' },
	lg: { box: 'w-6 h-6 rounded-md', icon: 16, label: 'text-base' },
}

function CheckIcon({ size }: { size: number }) {
	return (
		<motion.svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="3"
			strokeLinecap="round"
			strokeLinejoin="round"
			initial={{ pathLength: 0, opacity: 0 }}
			animate={{ pathLength: 1, opacity: 1 }}
			exit={{ pathLength: 0, opacity: 0 }}
			transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
		>
			<motion.polyline
				points="20 6 9 17 4 12"
				initial={{ pathLength: 0 }}
				animate={{ pathLength: 1 }}
				exit={{ pathLength: 0 }}
				transition={{ duration: 0.2 }}
			/>
		</motion.svg>
	)
}

function IndeterminateIcon({ size }: { size: number }) {
	return (
		<motion.svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="3"
			strokeLinecap="round"
			initial={{ opacity: 0, scaleX: 0 }}
			animate={{ opacity: 1, scaleX: 1 }}
			exit={{ opacity: 0, scaleX: 0 }}
			transition={{ duration: 0.15 }}
		>
			<line x1="5" y1="12" x2="19" y2="12" />
		</motion.svg>
	)
}

export function Checkbox({
	checked = false,
	indeterminate = false,
	onChange,
	label,
	hint,
	error,
	size = 'md',
	disabled,
	className,
	id: providedId,
	...props
}: CheckboxProps) {
	const generatedId = React.useId()
	const id = providedId || generatedId
	const s = sizeStyles[size]
	const isChecked = indeterminate || checked

	return (
		<div className={cn('flex items-start gap-2.5', className)}>
			<motion.button
				type="button"
				role="checkbox"
				id={id}
				aria-checked={indeterminate ? 'mixed' : checked}
				aria-invalid={!!error}
				disabled={disabled}
				onClick={() => !disabled && onChange?.(!checked)}
				className={cn(
					'flex items-center justify-center flex-shrink-0 border-2',
					'transition-colors duration-fast ease-default',
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40 focus-visible:ring-offset-2',
					'disabled:opacity-50 disabled:cursor-not-allowed',
					s.box,
					isChecked
						? 'bg-navy border-navy text-cream-50'
						: 'bg-background border-input hover:border-navy-300',
					error && !isChecked && 'border-error',
				)}
				whileTap={disabled ? undefined : { scale: 0.9 }}
				transition={{ type: 'spring', stiffness: 400, damping: 25 }}
			>
				<AnimatePresence mode="wait">
					{indeterminate ? (
						<IndeterminateIcon key="indeterminate" size={s.icon} />
					) : checked ? (
						<CheckIcon key="check" size={s.icon} />
					) : null}
				</AnimatePresence>
			</motion.button>
			{(label || hint || error) && (
				<div className="flex flex-col min-w-0 pt-px">
					{label && (
						<label
							htmlFor={id}
							className={cn('font-ui font-medium text-foreground cursor-pointer', s.label, disabled && 'opacity-50 cursor-not-allowed')}
						>
							{label}
						</label>
					)}
					{hint && !error && (
						<span className="text-xs text-muted-foreground font-ui">{hint}</span>
					)}
					{error && (
						<span className="text-xs text-error font-ui">{error}</span>
					)}
				</div>
			)}
		</div>
	)
}

// ─── Checkbox Group ─────────────────────────────────────────────────────────

export interface CheckboxGroupProps {
	/** Group label */
	label?: string
	/** Error for the group */
	error?: string
	/** Layout */
	horizontal?: boolean
	children: React.ReactNode
	className?: string
}

export function CheckboxGroup({ label, error, horizontal = false, children, className }: CheckboxGroupProps) {
	return (
		<fieldset className={cn('space-y-2', className)}>
			{label && (
				<legend className="text-sm font-medium font-ui text-foreground mb-2">{label}</legend>
			)}
			<div className={cn(horizontal ? 'flex flex-wrap gap-4' : 'flex flex-col gap-2')}>
				{children}
			</div>
			{error && (
				<p className="text-xs text-error font-ui mt-1">{error}</p>
			)}
		</fieldset>
	)
}
